import { useEffect, useRef, useState } from "react";
import {
    Box,
    Flex,
    VStack,
    HStack,
    Avatar,
    Text,
    Image,
    Input,
    Button,
    IconButton,
    StackDivider,
    useBreakpointValue,
    useColorModeValue,
    Alert,
    AlertIcon,
    AlertDescription, AlertTitle
} from "@chakra-ui/react";
import { CloseIcon, MinusIcon } from "@chakra-ui/icons";
import ChatHelpIcon from "../Icons/ChatHelpIcon";
import { CUSTOMER_HERO_IMAGE } from "../../constants/config";
import { ctbChatService } from "../../services/ctbChatService";
import Typing from "./Typing";
import { useTranslation } from "react-i18next";
import { KEYS } from "../../constants/keys";

export default function Chat({title, welcomeMessage, inputPlaceholder, loggedUserName, checkIsHealthy}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [isWaiting, setIsWaiting] = useState(false);
    const [greeting, setGreeting] = useState("");
    const [isHealthy, setIsHealthy] = useState(true);
    const chatEndRef = useRef(null);

    const user = JSON.parse(localStorage.getItem(KEYS.user) || "{}");
    const HISTORY_STORAGE_KEY = user.id ? String(user.id) : "temp_chat_history";

    const userBg = useColorModeValue("blue.500", "blue.300");
    const assistantBg = useColorModeValue("gray.100", "gray.700");
    const userColor = useColorModeValue("white", "black");
    const assistantColor = useColorModeValue("black", "white");
    const chatHeaderBg = useColorModeValue("gray.50", "gray.800");
    const chatBodyBg = useColorModeValue("white", "navy.800");
    const alertInfoBg = useColorModeValue("blue.50", "gray.800");
    const alertInfoColor = useColorModeValue("blue.700", "blue.200");

    const LOGO_URL = CUSTOMER_HERO_IMAGE ?? "/norteware-logo.webp"
    const TRANSLATE_BASE_PATH = "system.shared-components.chat";

    useEffect(() => {
        const savedMessages = localStorage.getItem(HISTORY_STORAGE_KEY);
        if (savedMessages) {
            try {
                setMessages(JSON.parse(savedMessages));
            } catch (err) {
                console.error(t("ctb-chat.messages.errors.historic"), err);
            }
        }
        checkIsHealthy();
    }, [HISTORY_STORAGE_KEY]);

    useEffect(() => {
        if (messages.length > 0) {
            const messagesToSave = messages.filter(msg => !msg.isTyping);

            localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(messagesToSave));
        }
    }, [messages, HISTORY_STORAGE_KEY]);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isOpen]);

    const getGreeting = () => {
        const now = new Date();
        const hour = now.getHours();

        if (hour >= 5 && hour < 12) {
            setGreeting(t(`${TRANSLATE_BASE_PATH}.greetings.good-morning`));
        } else if (hour >= 12 && hour < 18) {
            setGreeting(t(`${TRANSLATE_BASE_PATH}.greetings.good-afternoon`));
        } else if (hour >= 18 && hour < 22) {
            setGreeting(t(`${TRANSLATE_BASE_PATH}.greetings.good-evening`));
        } else {
            setGreeting(t(`${TRANSLATE_BASE_PATH}.greetings.good-night`));
        }
    };

    const handleOpen = async () => {
        getGreeting();
        if (checkIsHealthy) {
            const result = await checkIsHealthy();
            setIsHealthy(result);
        }
        setIsOpen(true);
    };

    const chatSize = useBreakpointValue({
        base: { w: "100vw", h: "100vh", rounded: "none" },
        md: { w: "400px", h: "600px", rounded: "lg" }
    });

    const handleSend = async () => {
        if (!input.trim() || isWaiting) return;

        const userMsg = { role: "user", content: input };
        const typingMsg = { role: "assistant", isTyping: true, component: <Typing /> };

        const base = Array.isArray(messages) ? messages : [];
        const nextMessages = [...base, userMsg, typingMsg];

        setMessages(nextMessages);

        const currentQuestion = input;
        setInput("");
        setIsWaiting(true);

        try {
            const response = await ctbChatService.ask(currentQuestion);

            setMessages((prev) =>
                prev.map((msg) => (msg.isTyping ? { role: "assistant", content: response.data === "" ? t(`${TRANSLATE_BASE_PATH}.empty-response`) : response.data } : msg))
            );
        } catch {

        } finally {
            setIsWaiting(false);
        }
    };

    return (
        <>
            {!isOpen && (
                <IconButton
                    icon={<ChatHelpIcon />}
                    colorScheme="blue"
                    rounded="full"
                    size="lg"
                    zIndex="popover"
                    position="fixed"
                    bottom={4}
                    right={4}
                    shadow="lg"
                    onClick={handleOpen}
                    aria-label={t(`system.actions.open`)}
                />
            )}

            {isOpen && (
                <Flex
                    direction="column"
                    bg={chatHeaderBg}
                    shadow="2xl"
                    zIndex="popover"
                    border="1px solid"
                    borderColor="gray.200"
                    position="fixed"
                    overflow="hidden"
                    bottom={{ base: 0, md: 4 }}
                    right={{ base: 0, md: 4 }}
                    {...chatSize}
                >

                    <HStack
                        p={3}
                        borderBottom="1px solid"
                        borderColor="gray.200"
                        justify="space-between"
                    >
                        <Text fontWeight="bold">{title}</Text>
                        <HStack>
                            <IconButton
                                icon={<MinusIcon />}
                                size="sm"
                                onClick={() => {
                                    setIsOpen(false);
                                    setIsWaiting(false);
                                }}
                                aria-label={t(`system.actions.close`)}
                            />
                            <IconButton
                                icon={<CloseIcon />}
                                size="sm"
                                onClick={() => {
                                    setIsOpen(false);
                                    setInput("");
                                    setIsWaiting(false);
                                }}
                                aria-label={t(`system.actions.close`)}
                            />
                        </HStack>
                    </HStack>

                    <Box flex="1" overflowY="auto" p={4} bg={chatBodyBg}>
                        {isHealthy ? (
                            <VStack
                                spacing={4}
                                align="stretch"
                                divider={<StackDivider borderColor="gray.200"/>}
                            >
                                <Box position="initial" top="0" zIndex="1000" >
                                    <Alert
                                        status="info"
                                        variant="subtle"
                                        fontSize="11px"
                                        rounded="lg"
                                        py={2}
                                        opacity={0.7}
                                        bg={alertInfoBg}
                                        color={alertInfoColor}
                                    >
                                        <AlertIcon h="16" w="16"/>
                                        {t(`${TRANSLATE_BASE_PATH}.default-info`)}
                                    </Alert>
                                </Box>
                                <HStack align="start">
                                    <Image
                                        src={LOGO_URL}
                                        alt="Norteware"
                                        w={8} h={8}
                                    />

                                    <Box
                                        bg={assistantBg}
                                        p={3}
                                        rounded="lg"
                                        shadow="sm"
                                        fontSize="sm"
                                    >
                                        <Text>{greeting} {welcomeMessage}</Text>
                                    </Box>
                                </HStack>

                                {messages.map((msg, idx) => {
                                    const prevMsg = messages[idx - 1];
                                    const showAvatar = !prevMsg || prevMsg.role !== msg.role;

                                    return (
                                        <HStack
                                            key={idx}
                                            align="start"
                                            justify={msg.role === "user" ? "flex-end" : "flex-start"}
                                        >
                                            {msg.role !== "user" && showAvatar && <Image
                                                src={LOGO_URL}
                                                alt="Norteware"
                                                w={8} h={8}
                                            />}
                                            <Box
                                                bg={msg.role === "user" ? userBg : assistantBg}
                                                color={msg.role === "user" ? userColor : assistantColor}
                                                p={3}
                                                rounded="lg"
                                                fontSize="sm"
                                                shadow="sm"
                                                fontStyle={msg.isTyping ? "italic" : "normal"}
                                            >
                                                {msg.component ? msg.component : <Text>{msg.content}</Text>}
                                            </Box>
                                            {msg.role === "user" && showAvatar && <Avatar
                                                color="white"
                                                fontWeight="bold"
                                                size="sm"
                                                name={loggedUserName}
                                            />}
                                        </HStack>
                                    );
                                })}
                                <div ref={chatEndRef}/>
                            </VStack>
                        ) : (
                            <Alert status="warning" borderRadius="md">
                                <AlertIcon />
                                <AlertTitle fontSize="sm">
                                    {t(`${TRANSLATE_BASE_PATH}.warnings.service-unavailable.title`)}
                                </AlertTitle>
                                <AlertDescription fontSize="sm">
                                    {t(`${TRANSLATE_BASE_PATH}.warnings.service-unavailable.description`)}
                                </AlertDescription>
                            </Alert>
                        )}
                    </Box>

                    <HStack bgColor={chatBodyBg} p={3} borderTop="1px solid" borderColor="gray.200">
                        <Input
                            placeholder={inputPlaceholder}
                            value={input}
                            fontSize="sm"
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSend()}
                            isDisabled={isWaiting || !isHealthy}
                        />
                        <Button
                            fontSize="sm"
                            colorScheme="blue"
                            onClick={handleSend}
                            isDisabled={isWaiting || !isHealthy}
                        >
                            {t(`system.actions.send`)}
                        </Button>
                    </HStack>
                </Flex>
            )}
        </>
    );
}
